import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Clock, Facebook, Twitter, Instagram, Linkedin, Youtube } from 'lucide-react';
import './Footer.css';

// Custom TikTok Icon Component
const TikTokIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M19.321 5.562a5.124 5.124 0 0 1-.443-.258 6.228 6.228 0 0 1-1.137-.966c-.849-.966-1.374-2.201-1.374-3.548v-.79h-3.15v14.844c0 .38-.086.753-.25 1.094a3.351 3.351 0 0 1-.678.97 3.277 3.277 0 0 1-1.019.673 3.204 3.204 0 0 1-1.187.225c-.885 0-1.729-.351-2.347-.975a3.359 3.359 0 0 1-.973-2.378c0-.885.35-1.733.973-2.357a3.275 3.275 0 0 1 2.347-.975c.336 0 .669.053.987.158v-3.26a6.486 6.486 0 0 0-.987-.075 6.617 6.617 0 0 0-4.675 1.937 6.728 6.728 0 0 0-1.938 4.736c0 1.789.71 3.504 1.938 4.736a6.617 6.617 0 0 0 4.675 1.937c1.789 0 3.504-.71 4.736-1.937a6.728 6.728 0 0 0 1.937-4.736V9.225a9.42 9.42 0 0 0 5.36 1.664V7.74a6.195 6.195 0 0 1-2.99-2.178z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [contactInfo, setContactInfo] = useState({
    emailAddress: '',
    phoneNumber: '',
    address: '',
    workingHours: '',
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: '',
      tiktok: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const baseURL="https://backendweb.properteck.lk";

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await fetch(`${baseURL}/api/contact/get`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch contact information');
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          setContactInfo(result.data);
        } else {
          throw new Error('Invalid data format received');
        }
      } catch (err) {
        console.error('Error fetching contact information:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);
  
  return (
    <footer className="footer">
      <div className="footer-container">
        {/* Main Footer Content */}
        <div className="footer-content">
          {/* Company Section */}
          <div className="footer-section">
            <h3 className="footer-heading">PROPERTECK</h3>
            <p className="company-description">
              Your trusted partner in property technology solutions, bringing innovation to real estate since 2020.
            </p>
            <div className="social-icons">
              {contactInfo.socialMedia.facebook && (
                <a href={contactInfo.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="social-icon">
                  <Facebook size={20} />
                </a>
              )}
              {contactInfo.socialMedia.twitter && (
                <a href={contactInfo.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="social-icon">
                  <Twitter size={20} />
                </a>
              )}
              {contactInfo.socialMedia.instagram && (
                <a href={contactInfo.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="social-icon">
                  <Instagram size={20} />
                </a>
              )}
              {contactInfo.socialMedia.linkedin && (
                <a href={contactInfo.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="social-icon">
                  <Linkedin size={20} />
                </a>
              )}
              {contactInfo.socialMedia.youtube && (
                <a href={contactInfo.socialMedia.youtube} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="YouTube">
                  <Youtube size={20} />
                </a>
              )}
              {contactInfo.socialMedia.tiktok && (
                <a href={contactInfo.socialMedia.tiktok} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="TikTok">
                 <TikTokIcon size={20}/>
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-section">
            <h3 className="footer-heading">Quick Links</h3>
            <ul className="footer-links">
              <li>
                <a href="/about" className="footer-link">
                  <span className="link-arrow">›</span> About Us
                </a>
              </li>
              <li>
                <a href="/service" className="footer-link">
                  <span className="link-arrow">›</span> Services
                </a>
              </li>  
              <li>
                <a href="/contact" className="footer-link">
                  <span className="link-arrow">›</span> Contact Us
                </a>
              </li>
              {/*}
              <li>
                <a href="/appointment" className="footer-link">
                  <span className="link-arrow">›</span> Appointment
                </a>
              </li>
              <li>
                <a href="#" className="footer-link">
                  <span className="link-arrow">›</span> FAQ
                </a>
              </li>
              */}
            </ul>
          </div>
          
          {/* Contact Information */}
          <div className="footer-section">
            <h3 className="footer-heading">Contact Us</h3>
            {loading ? (
              <p>Loading contact information...</p>
            ) : error ? (
              <p>Error loading contact information. Please try again later.</p>
            ) : (
              <ul className="contact-list">
                {contactInfo.emailAddress && (
                  <li className="contact-item">
                    <Mail className="contact-icon" size={18} />
                    <span>{contactInfo.emailAddress}</span>
                  </li>
                )}
                {contactInfo.phoneNumber && (
                  <li className="contact-item">
                    <Phone className="contact-icon" size={18} />
                    <span>{contactInfo.phoneNumber}</span>
                  </li>
                )}
                {contactInfo.address && (
                  <li className="contact-item">
                    <MapPin className="contact-icon" size={18} />
                    <span>{contactInfo.address}</span>
                  </li>
                )}
                {contactInfo.workingHours && (
                  <li className="contact-item">
                    <Clock className="contact-icon" size={18} />
                    <span>{contactInfo.workingHours}</span>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="footer-divider"></div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-left">
            <p className="copyright">
              © {currentYear} PROPERTECK. All Rights Reserved.
            </p>
            <p className="developer-credit">
              Developed by{' '}
              <a 
                href="https://www.linkedin.com/in/pasindu-udayanga" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="developer-link"
              >
                Pasindu Udayanga
              </a>
            </p>
          </div>
          <div className="legal-links">
            <a href="/privacypolicy" className="legal-link" target="_blank">Privacy Policy</a>
            <a href="/termsconditions" className="legal-link" target="_blank">Terms of Service</a>
            <a href="/returnpolicy" className="legal-link" target="_blank">Return Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;